/**
 * components/ui/AppLinkButton.js
 * ─────────────────────────────────────────────────────────────────────────────
 * A button that TAKES YOU to the next step, painted exactly like AppButton.
 *
 * Built for the Tax pages (23 Oct 2026), where every state that implies a next
 * step now carries the step as a control: an unmapped product links to mapping
 * it, a failing health check links to where it is fixed, a vendor's enrolment
 * stage links to the vendors waiting on it. A plain text link was not enough:
 * on this dashboard a control that does something is a filled navy rounded
 * rectangle, and a link that looks like body text is read as body text.
 *
 * One of three destinations, decided by which prop is given:
 *   to       a dashboard path; navigates in place (react-router Link)
 *   href     an outside page (URA's portal); opens in its own tab, and says so
 *            with a trailing icon
 *   anchor   the id of a panel on this page; scrolls it into view
 *
 *   <AppLinkButton to="/admin/fiscal-documents?status=REJECTED" size="sm">
 *     Show rejected documents
 *   </AppLinkButton>
 */
import React from "react";
import { Link } from "react-router-dom";
import { appButtonClassName } from "./AppButton.js";
import "./ui.css";

const AppLinkButton = ({ to, href, anchor, variant = "primary", size = "md", icon, className, children, ...rest }) => {
  const classes = appButtonClassName({ variant, size, className });
  const lead = icon ? <i className={icon} aria-hidden="true" /> : null;

  if (to) {
    return (
      <Link to={to} className={classes} {...rest}>
        {lead}
        {children}
      </Link>
    );
  }
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classes} {...rest}>
        {lead}
        {children}
        <i className="fas fa-external-link-alt" aria-hidden="true" style={{ fontSize: "0.85em" }} />
        <span className="visually-hidden"> (opens in a new tab)</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      className={classes}
      onClick={() => {
        const target = anchor ? document.getElementById(anchor) : null;
        if (target && target.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "start" });
      }}
      {...rest}
    >
      {lead}
      {children}
    </button>
  );
};

export default AppLinkButton;
